import React, { useState, useEffect } from 'react';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { useToast } from '../../components/ToastProvider';
import { usePincodeValidation } from '../../hooks/usePincodeValidation';

// Custom Confirmation Modal Component
const ConfirmModal = ({ isOpen, title, message, onConfirm, onCancel }) => {
    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(4px)'
        }}>
            <div style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '30px',
                maxWidth: '400px',
                width: '90%',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                animation: 'modalFadeIn 0.2s ease-out'
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px'
                }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3,6 5,6 21,6"></polyline>
                        <path d="M19,6v14a2,2 0 0,1-2,2H7a2,2 0 0,1-2-2V6m3,0V4a2,2 0 0,1,2-2h4a2,2 0 0,1,2,2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                </div>
                <h3 style={{
                    margin: '0 0 10px',
                    textAlign: 'center',
                    fontSize: '1.25rem',
                    color: '#1f2937',
                    fontWeight: '600'
                }}>{title}</h3>
                <p style={{
                    margin: '0 0 25px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '0.95rem',
                    lineHeight: '1.5'
                }}>{message}</p>
                <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center'
                }}>
                    <button
                        onClick={onCancel}
                        style={{
                            padding: '12px 28px',
                            borderRadius: '10px',
                            border: '1px solid #e5e7eb',
                            background: '#fff',
                            color: '#374151',
                            fontSize: '0.95rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.background = '#f9fafb';
                            e.target.style.borderColor = '#d1d5db';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.background = '#fff';
                            e.target.style.borderColor = '#e5e7eb';
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        style={{
                            padding: '12px 28px',
                            borderRadius: '10px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                            color: '#fff',
                            fontSize: '0.95rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 4px 14px rgba(220, 38, 38, 0.25)'
                        }}
                        onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.35)';
                        }}
                        onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 14px rgba(220, 38, 38, 0.25)';
                        }}
                    >
                        Delete
                    </button>
                </div>
            </div>
            <style>{`
                @keyframes modalFadeIn {
                    from {
                        opacity: 0;
                        transform: scale(0.95) translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }
                /* Responsive Grid for Address Form */
                .address-form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr;
                    gap: 10px;
                }
                @media (max-width: 480px) {
                    .address-form-grid {
                        grid-template-columns: 1fr; /* Stack vertically on mobile */
                        gap: 15px; /* More space between stacked items */
                    }
                }
            `}</style>
        </div>
    );
};

const Addresses = () => {
    const { csrfToken } = useCustomerAuth();
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ label: '', address: '', city: '', state: '', pincode: '', is_default: false });
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, addressId: null, addressLabel: '' });
    const [pincodeWarning, setPincodeWarning] = useState(null);
    const { showToast } = useToast();
    const { validate } = usePincodeValidation();

    const BASE_URL = process.env.REACT_APP_API_BASE || 'https://handmade-haven.in';

    const fetchAddresses = async () => {
        try {
            const res = await fetch(`${BASE_URL}/api/customer/addresses.php`, { credentials: 'include' });
            const data = await res.json();
            if (data.ok) setAddresses(data.addresses);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAddresses();
    }, []);

    const openDeleteConfirm = (addr) => {
        setDeleteConfirm({ isOpen: true, addressId: addr.id, addressLabel: addr.label });
    };

    const closeDeleteConfirm = () => {
        setDeleteConfirm({ isOpen: false, addressId: null, addressLabel: '' });
    };

    const handleDelete = async () => {
        const id = deleteConfirm.addressId;
        closeDeleteConfirm();
        try {
            const res = await fetch(`${BASE_URL}/api/customer/address-delete.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken
                },
                body: JSON.stringify({ id }),
                credentials: 'include'
            });
            if (res.ok) fetchAddresses();
        } catch (err) {
            showToast('Error deleting address', 'error');
        }
    };

    const handleEditStart = (addr) => {
        setForm({
            label: addr.label,
            address: addr.address,
            city: addr.city,
            state: addr.state,
            pincode: addr.pincode,
            is_default: !!addr.is_default // ensure boolean
        });
        setEditingId(addr.id);
        setIsAdding(true);
        // Scroll to form slightly
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancel = () => {
        setIsAdding(false);
        setEditingId(null);
        setForm({ label: '', address: '', city: '', state: '', pincode: '', is_default: false });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const endpoint = editingId ? 'address-update.php' : 'address-create.php';
            const payload = editingId ? { ...form, id: editingId } : form;

            const res = await fetch(`${BASE_URL}/api/customer/${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': csrfToken
                },
                body: JSON.stringify(payload),
                credentials: 'include'
            });

            const data = await res.json();
            if (data.ok) {
                showToast(editingId ? 'Address updated' : 'Address added', 'success');
                handleCancel(); // Reset form and mode
                fetchAddresses();
            } else {
                showToast(data.message, 'error');
            }
        } catch (err) {
            showToast('Error saving address', 'error');
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2 className="account-section-title" style={{ marginBottom: 0, border: 'none' }}>Addresses</h2>
                <button
                    onClick={() => {
                        if (isAdding) handleCancel();
                        else setIsAdding(true);
                    }}
                    className="btn-login"
                    style={{ width: 'auto', padding: '8px 20px', fontSize: '0.9rem' }}
                >
                    {isAdding ? 'Cancel' : '+ Add New'}
                </button>
            </div>

            {isAdding && (
                <form onSubmit={handleSubmit} style={{ background: '#f9f9f9', padding: '20px', borderRadius: '10px', marginBottom: '20px', border: '1px solid #eee' }}>
                    <h4 style={{ marginBottom: '15px', color: '#333' }}>{editingId ? 'Edit Address' : 'New Address'}</h4>
                    <div className="form-group"><input placeholder="Label (e.g. Home)" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} required /></div>
                    <div className="form-group"><input placeholder="Address" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} required /></div>
                    <div className="address-form-grid">
                        <div className="form-group"><input placeholder="City" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} required /></div>
                        <div className="form-group"><input placeholder="State" value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} required /></div>
                        <div className="form-group">
                            <input
                                placeholder="Pincode"
                                value={form.pincode}
                                onChange={e => {
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                    setForm({ ...form, pincode: value });
                                    // Validate when 6 digits entered
                                    if (value.length === 6) {
                                        const result = validate(value);
                                        if (!result.valid) {
                                            setPincodeWarning('Delivery may not be available to this location. We only deliver within Gujarat.');
                                        } else {
                                            setPincodeWarning(null);
                                        }
                                    } else {
                                        setPincodeWarning(null);
                                    }
                                }}
                                required
                                maxLength={6}
                                style={pincodeWarning ? { borderColor: '#f59e0b', background: '#fffbeb' } : {}}
                            />
                        </div>
                    </div>
                    {pincodeWarning && (
                        <div style={{
                            marginTop: '8px',
                            padding: '10px 12px',
                            fontSize: '0.85rem',
                            color: '#92400e',
                            backgroundColor: '#fef3c7',
                            border: '1px solid #fcd34d',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <span style={{ fontSize: '1rem' }}>⚠️</span>
                            <span>{pincodeWarning}</span>
                        </div>
                    )}
                    <div className="form-group">
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', cursor: 'pointer' }}>
                            <input type="checkbox" style={{ width: 'auto' }} checked={form.is_default} onChange={e => setForm({ ...form, is_default: e.target.checked })} />
                            Set as Default Address
                        </label>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button type="submit" className="btn-login" style={{ flex: 1 }}>{editingId ? 'Update Address' : 'Save Address'}</button>
                        <button type="button" onClick={handleCancel} className="btn-login" style={{ width: 'auto', background: '#ccc', flex: 0.3 }}>Cancel</button>
                    </div>
                </form>
            )}

            <div style={{ display: 'grid', gap: '15px' }}>
                {addresses.map(addr => (
                    <div key={addr.id} style={{
                        border: addr.is_default ? '2px solid #ffb6c9' : '1px solid #eee',
                        borderRadius: '12px',
                        padding: '20px',
                        position: 'relative',
                        background: addr.is_default ? '#fff9fa' : '#fff'
                    }}>
                        {addr.is_default && <span style={{ position: 'absolute', top: '15px', right: '15px', fontSize: '0.8rem', color: '#d14c87', fontWeight: 'bold' }}>DEFAULT</span>}
                        <h4 style={{ margin: '0 0 5px', color: '#333' }}>{addr.label}</h4>
                        <p style={{ margin: '0 0 5px', color: '#666', fontSize: '0.95rem' }}>{addr.address}</p>
                        <p style={{ margin: '0', color: '#888', fontSize: '0.9rem' }}>{addr.city}, {addr.state} - {addr.pincode}</p>
                        <div style={{ marginTop: '15px', display: 'flex', gap: '15px' }}>
                            <button
                                onClick={() => handleEditStart(addr)}
                                style={{ background: 'none', border: 'none', color: '#36454F', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline', fontWeight: 500 }}
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => openDeleteConfirm(addr)}
                                style={{ background: 'none', border: 'none', color: '#c53030', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
                {addresses.length === 0 && !loading && !isAdding && <p style={{ color: '#666' }}>No addresses added yet.</p>}
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                title="Delete Address"
                message={`Are you sure you want to delete "${deleteConfirm.addressLabel}"? This action cannot be undone.`}
                onConfirm={handleDelete}
                onCancel={closeDeleteConfirm}
            />
        </div>
    );
};

export default Addresses;
